import React, { useMemo, useState, useCallback } from "react";
import { Button, ButtonGroup, Badge, ProgressBar } from "react-bootstrap";
import { FiCopy, FiEdit, FiTrash2, FiEye } from "react-icons/fi";
import { usePromotionalCodes } from "../../hooks/usePromotionalCodes";
import DataTable from "components/common/DataTable";
import { createColumn } from "components/common/DataTable/utils";
import PromotionalCodeFilters from "./PromotionalCodeFilters";
import PromotionalCodeBulkActions from "./PromotionalCodeBulkActions";
import {
  getStatusBadgeVariant,
  getDiscountTypeBadgeVariant,
  getCouponTypeBadgeVariant,
  DISCOUNT_TYPE_LABELS,
  COUPON_TYPE_LABELS,
  STATUS_LABELS,
  formatUsageCount,
  copyCodeToClipboard,
  calculateCodeStatus,
  CODE_STATUS,
} from "utils/promotionalCode";
import { formatDate } from "utils/date";
import { toast } from "react-toastify";
import "./PromotionalCodes.scss";

const PromotionalCodesList = ({ onCreate, onEdit, onDelete, onViewDetails }) => {
  const [filters, setFilters] = useState({
    status: "",
    couponType: "",
    discountType: "",
    search: "",
    page: 1,
    limit: 20,
  });
  const [selectedCodes, setSelectedCodes] = useState([]);

  const { codes, pagination, isLoading, error } = usePromotionalCodes(filters);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  }, []);

  const handlePageChange = useCallback((page) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((newSize) => {
    setFilters((prev) => ({ ...prev, limit: newSize, page: 1 }));
  }, []);

  const handleCopyCode = useCallback(async (code) => {
    const success = await copyCodeToClipboard(code);
    if (success) {
      toast.success("Code copied to clipboard!");
    } else {
      toast.error("Failed to copy code");
    }
  }, []);

  const columns = useMemo(
    () => [
      createColumn({
        id: "code",
        header: "Code",
        accessorKey: "code",
        cell: ({ row }) => (
          <div className="d-flex align-items-center gap-2">
            <code className="promo-code-text">{row.original.code}</code>
            <Button
              variant="link"
              size="sm"
              className="p-0 copy-button"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyCode(row.original.code);
              }}
              title="Copy code"
            >
              <FiCopy size={16} />
            </Button>
          </div>
        ),
        enableSorting: true,
      }),
      createColumn({
        id: "discountType",
        header: "Discount Type",
        accessorKey: "discountType",
        cell: ({ row }) => (
          <Badge bg={getDiscountTypeBadgeVariant(row.original.discountType)}>
            {DISCOUNT_TYPE_LABELS[row.original.discountType] || row.original.discountType}
          </Badge>
        ),
        enableSorting: true,
      }),
      createColumn({
        id: "couponType",
        header: "Coupon Type",
        accessorKey: "couponType",
        cell: ({ row }) => (
          <Badge bg={getCouponTypeBadgeVariant(row.original.couponType)}>
            {COUPON_TYPE_LABELS[row.original.couponType] || row.original.couponType}
          </Badge>
        ),
        enableSorting: true,
      }),
      createColumn({
        id: "status",
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => {
          const calculatedStatus = calculateCodeStatus(row.original);
          return (
            <Badge bg={getStatusBadgeVariant(calculatedStatus)}>
              {STATUS_LABELS[calculatedStatus] || calculatedStatus}
            </Badge>
          );
        },
        enableSorting: true,
      }),
      createColumn({
        id: "usage",
        header: "Usage",
        accessorFn: (row) => row.usedCount || 0,
        cell: ({ row }) => {
          const usedCount = row.original.usedCount || 0;
          const maxUses = row.original.maxUses;
          const usagePercentage = maxUses && maxUses > 0 ? (usedCount / maxUses) * 100 : null;

          return (
            <div>
              <div>{formatUsageCount(usedCount, maxUses)}</div>
              {usagePercentage !== null && (
                <ProgressBar
                  now={usagePercentage}
                  variant={usagePercentage >= 100 ? "danger" : usagePercentage >= 80 ? "warning" : "success"}
                  className="usage-progress-bar"
                  style={{ height: "6px", marginTop: "4px" }}
                />
              )}
            </div>
          );
        },
        enableSorting: true,
      }),
      createColumn({
        id: "expiresAt",
        header: "Expiry Date",
        accessorKey: "expiresAt",
        cell: ({ row }) => formatDate(row.original.expiresAt),
        enableSorting: true,
      }),
      createColumn({
        id: "createdAt",
        header: "Created Date",
        accessorKey: "createdAt",
        cell: ({ row }) => formatDate(row.original.createdAt),
        enableSorting: true,
      }),
      createColumn({
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <ButtonGroup size="sm" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="outline-info"
              onClick={() => onViewDetails(row.original.id)}
              title="View Details"
            >
              <FiEye size={16} />
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => onEdit(row.original)}
              title="Edit"
            >
              <FiEdit size={16} />
            </Button>
            <Button
              variant="outline-danger"
              onClick={() => onDelete(row.original)}
              title="Delete"
            >
              <FiTrash2 size={16} />
            </Button>
          </ButtonGroup>
        ),
        enableSorting: false,
      }),
    ],
    [onEdit, onDelete, onViewDetails, handleCopyCode]
  );

  const handleSelectionChange = useCallback((selectedRows) => {
    setSelectedCodes(selectedRows);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedCodes([]);
  }, []);

  const rowSelectionConfig = useMemo(
    () => ({
      enabled: true,
      onSelectionChange: handleSelectionChange,
    }),
    [handleSelectionChange]
  );

  const paginationConfig = useMemo(
    () => ({
      enabled: true,
      pageSize: filters.limit,
      pageIndex: filters.page - 1,
      pageCount: pagination?.pages || pagination?.totalPages || 1,
      totalCount: pagination?.total || 0,
      manual: true,
      onPageChange: handlePageChange,
      onPageSizeChange: handlePageSizeChange,
    }),
    [filters.limit, filters.page, pagination?.pages, pagination?.totalPages, pagination?.total, handlePageChange, handlePageSizeChange]
  );

  if (error) {
    return <div className="text-danger">Error loading promotional codes: {error.message}</div>;
  }

  const tableData = codes || [];

  return (
    <div className="promotional-codes-list">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Promotional Codes</h4>
        <Button variant="primary" onClick={onCreate}>
          Create Promotional Code
        </Button>
      </div>

      <PromotionalCodeFilters filters={filters} onFilterChange={handleFilterChange} />

      <PromotionalCodeBulkActions selectedCodes={selectedCodes} onClearSelection={handleClearSelection} />

      <DataTable
        data={tableData}
        columns={columns}
        loading={isLoading}
        pagination={paginationConfig}
        sorting={{ enabled: true, manual: false }}
        filtering={{ enabled: false }}
        rowSelection={rowSelectionConfig}
        keyField="id"
        striped
        bordered
        hover
        responsive
        emptyMessage="No promotional codes found"
      />
    </div>
  );
};

export default PromotionalCodesList;

